import { ButtonPrimary, ButtonSecondary } from 'components/Button'
import QuestionHelper from 'components/QuestionHelper'
import Tag from 'components/Tag'
import { useActiveWeb3React } from 'hooks'
import { REFERRAL_FOR_USERS_PARAMS } from 'pages/utils/ReferralHandler'
import React, { useState } from 'react'
import styled from 'styled-components'
import { usePrivy } from '@privy-io/react-auth'
import { roundToDecimalPlace } from 'utils/data'
import { Text } from 'rebass'
import axios from 'axios'
import { API_POOL_BASE_URL } from 'constants/links'
import Loader from 'components/Loader'
import { ecosystemList } from '../Points/list'
const RankContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`

const ItemContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 24px;
  border-radius: 16px;
  background: ${({ theme }) => theme.bg1};
  overflow: auto;

  .title {
    color: ${({ theme }) => theme.text2};
    font-size: 16px;
    font-weight: 500;
  }

  .value {
    font-family: Unbounded;
    color: ${({ theme }) => theme.primary1};
    font-size: 40px;
    font-weight: 700;
    @media (max-width: 768px) {
      font-size: 32px;
    }
  }
`
const UsernameContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 24px;
  border-radius: 16px;
  background: ${({ theme }) => theme.bg1};
  overflow: auto;

  .title {
    color: ${({ theme }) => theme.text2};
    font-size: 16px;
    font-weight: 500;
  }

  .value {
    font-family: Unbounded;
    color: ${({ theme }) => theme.text1};
    font-size: 18px;
    font-weight: 500;

    @media (max-width: 768px) {
      font-size: 14px;
    }
  }
`

const MobileOnly = styled.div`
  @media (min-width: 768px) {
    display: none;
  }
`

const Rank: React.FC<{
  isTestnetUser: boolean
  isReferrer: boolean
  points: number
  username: string | null
  openModal: () => void
  pointsDetails: any[]
}> = ({ isTestnetUser, isReferrer, points, username, openModal, pointsDetails }) => {
  const { account } = useActiveWeb3React()
  const { login } = usePrivy()
  const [ranking, setRanking] = React.useState(0)
  const [loading, setLoading] = React.useState(false)

  React.useEffect(() => {
    if (account) {
      setLoading(true)
      axios
        .get(`${API_POOL_BASE_URL}/api/get-id`, {
          params: { address: account },
        })
        .then((response: any) => {
          if (response.status === 200) {
            setRanking(response.data.rank)
          }
        })
        .catch((e: any) => console.warn(e))
        .finally(() => {
          setLoading(false)
        })
    }
  }, [account])

  const [copyStatus, setCopyStatus] = useState<string | null>(null) // State to manage copy status
  const saveLink = () => {
    if (username) {
      const referralLink = `${window.location.origin}/#/swap?${REFERRAL_FOR_USERS_PARAMS}=${username}`
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard
          .writeText(referralLink)
          .then(() => {
            setCopyStatus('Copied!')
            setTimeout(() => setCopyStatus(null), 5000)
          })
          .catch(() => {
            // Fallback to manual copy if writeText fails
            fallbackCopyTextToClipboard(referralLink)
          })
      } else {
        // Fallback for older browsers
        fallbackCopyTextToClipboard(referralLink)
      }
    }
  }

  // Fallback function using document.execCommand('copy')
  const fallbackCopyTextToClipboard = (text: string) => {
    const textArea = document.createElement('textarea')
    textArea.value = text
    textArea.style.position = 'fixed' // Prevent scrolling to bottom of page in Microsoft Edge.
    document.body.appendChild(textArea)
    textArea.focus()
    textArea.select()

    try {
      const successful = document.execCommand('copy')
      if (successful) {
        setCopyStatus('Copied!')
      } else {
        setCopyStatus('Copy failed')
      }
    } catch (err) {
      console.error('Fallback: Oops, unable to copy', err)
    }

    document.body.removeChild(textArea)
    setTimeout(() => setCopyStatus(null), 5000)
  }

  let totalPoints = points
  pointsDetails.forEach((partner) => {
    const ecosystemInfo = ecosystemList?.[partner.name]

    if (ecosystemInfo?.disabled === true) {
      return
    }

    totalPoints = totalPoints + partner.total
  })

  return (
    <>
      <RankContainer>
        <ItemContainer>
          <span className="title" style={{ display: 'flex', gap: '6px' }}>
            Your points <QuestionHelper text="Note: The points will be updated every Sunday at 10PM PST." />
          </span>
          <span className="value">{roundToDecimalPlace(`${totalPoints}`, '0')}</span>
        </ItemContainer>
        <ItemContainer>
          <span className="title">Your rank</span>
          {loading ? <Loader size="42px"></Loader> : <span className="value">{ranking ? ranking : 'N/A'}</span>}
        </ItemContainer>
        <UsernameContainer>
          <span className="title">Username</span>
          <div style={{ display: 'flex', gap: '8px', flexDirection: 'column' }}>
            {account ? (
              <>
                {username ? (
                  <>
                    <span className="value">{username}</span>

                    <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                      {isTestnetUser && (
                        <>
                          <Tag variant={2}>
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="14"
                              height="14"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              className="icon icon-tabler icons-tabler-outline icon-tabler-progress-check"
                            >
                              <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                              <path d="M10 20.777a8.942 8.942 0 0 1 -2.48 -.969" />
                              <path d="M14 3.223a9.003 9.003 0 0 1 0 17.554" />
                              <path d="M4.579 17.093a8.961 8.961 0 0 1 -1.227 -2.592" />
                              <path d="M3.124 10.5c.16 -.95 .468 -1.85 .9 -2.675l.169 -.305" />
                              <path d="M6.907 4.579a8.954 8.954 0 0 1 3.093 -1.356" />
                              <path d="M9 12l2 2l4 -4" />
                            </svg>
                            Testnet User
                          </Tag>
                        </>
                      )}
                      {isReferrer && (
                        <>
                          <Tag variant={2}>
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="14"
                              height="14"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              className="icon icon-tabler icons-tabler-outline icon-tabler-users-group"
                            >
                              <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                              <path d="M10 13a2 2 0 1 0 4 0a2 2 0 0 0 -4 0" />
                              <path d="M8 21v-1a2 2 0 0 1 2 -2h4a2 2 0 0 1 2 2v1" />
                              <path d="M15 5a2 2 0 1 0 4 0a2 2 0 0 0 -4 0" />
                              <path d="M17 10h2a2 2 0 0 1 2 2v1" />
                              <path d="M5 5a2 2 0 1 0 4 0a2 2 0 0 0 -4 0" />
                              <path d="M3 13v-1a2 2 0 0 1 2 -2h2" />
                            </svg>
                            Community Referrer
                          </Tag>
                        </>
                      )}
                    </div>
                  </>
                ) : (
                  <>
                    <ButtonSecondary onClick={() => openModal()}>Sign up</ButtonSecondary>
                  </>
                )}
              </>
            ) : (
              <>
                <ButtonPrimary
                  onClick={() =>
                    login({
                      loginMethods: ['wallet'],
                      walletChainType: 'ethereum-only',
                      disableSignup: false,
                    })
                  }
                >
                  Connect Wallet
                </ButtonPrimary>
              </>
            )}
          </div>
        </UsernameContainer>
      </RankContainer>

      {username ? (
        <MobileOnly>
          <ButtonSecondary id="copy" style={{ padding: 16 }} as={'button'} onClick={saveLink}>
            <Text fontWeight={400} fontSize={14} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              {copyStatus || 'Copy my referral link'}
            </Text>
          </ButtonSecondary>
        </MobileOnly>
      ) : (
        <></>
      )}
    </>
  )
}
export default Rank
